package com.exura.components.inputs

import androidx.compose.runtime.Composable
import com.exura.components.pageTypography.ExuraErrorTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.ExuraUnderwriteTextVariant
import com.exura.primary
import com.exura.transparent
import com.exura.ui.Dimens
import com.exura.ui.Durations
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Outline
import com.varabyte.kobweb.compose.css.Resize
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.resize
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.selectors.focus
import com.varabyte.kobweb.silk.style.selectors.placeholder
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.placeholder
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.readOnly
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.TextArea

@Composable
fun InlineTextInputWithError(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    variant: CssStyleVariant<InlineInputKind>? = null,
    placeholderText: String = "",
    inputType: InputType<String> = InputType.Text,
    transformValue: ((String) -> String)? = null,
    isEnabled: Boolean = true,
    isReadOnly: Boolean = false,
    error: String = "",
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(Dimens.pageBodyUnderwritingGap)
    ) {
        InlineTextInput(
            value = value,
            onValueChange = onValueChange,
            modifier = Modifier.fillMaxWidth(),
            variant = variant,
            placeholderText = placeholderText,
            inputType = inputType,
            isEnabled = isEnabled,
            isReadOnly = isReadOnly,
            transformValue = transformValue,
        )
        SpanText(
            modifier = ExuraTextStyle
                .toModifier(ExuraErrorTextVariant, ExuraUnderwriteTextVariant),
            text = error,
        )
    }
}


@Composable
fun InlineTextInputWithAction(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    variant: CssStyleVariant<InlineInputKind>? = null,
    placeholderText: String = "",
    inputType: InputType<String> = InputType.Text,
    transformValue: ((String) -> String)? = null,
    isEnabled: Boolean = true,
    isReadOnly: Boolean = false,
    actionButton: (@Composable () -> Unit)? = null,
) {
    if (actionButton == null) {
        InlineTextInput(
            value = value,
            onValueChange = onValueChange,
            modifier = modifier,
            variant = variant,
            placeholderText = placeholderText,
            inputType = inputType,
            isEnabled = isEnabled,
            isReadOnly = isReadOnly,
            transformValue = transformValue,
        )
    } else {
        Box(
            modifier = modifier,
            contentAlignment = Alignment.CenterEnd
        ) {
            InlineTextInput(
                value = value,
                onValueChange = onValueChange,
                modifier = Modifier.fillMaxWidth(),
                variant = variant,
                placeholderText = placeholderText,
                inputType = inputType,
                isEnabled = isEnabled,
                isReadOnly = isReadOnly,
                transformValue = transformValue,
            )

            Box(
                modifier = Modifier.padding(right = Dimens.inputIndent)
            ) {
                actionButton()
            }
        }
    }
}


@Composable
internal fun InlineTextInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    variant: CssStyleVariant<InlineInputKind>? = null,
    placeholderText: String = "",
    inputType: InputType<String> = InputType.Text,
    transformValue: ((String) -> String)? = null,
    isEnabled: Boolean = true,
    isReadOnly: Boolean = false,
) {
    Input(
        type = inputType,
        attrs = InlineInputStyle.toModifier(
            variant,
            if (isReadOnly) InlineInputReadOnlyVariant else null,
            if (!isEnabled) InlineInputDisabledVariant else null,
        )
            .then(modifier)
            .toAttrs {
                placeholder(placeholderText)
                value(value)
                if (!isEnabled) disabled()
                if (isReadOnly) readOnly()
                onInput {
                    val newValue = if (transformValue != null) {
                        transformValue(it.value)
                    } else {
                        it.value
                    }
                    onValueChange(newValue)
                }
            }
    )
}

@Composable
internal fun TextAreaInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    variant: CssStyleVariant<InlineInputKind>? = null,
    placeholderText: String = "",
    transformValue: ((String) -> String)? = null,
    isEnabled: Boolean = true,
    isReadOnly: Boolean = false,
) {
    TextArea(
        value = value,
        attrs = InlineInputStyle.toModifier(
            variant,
            if (isReadOnly) InlineInputReadOnlyVariant else null,
            if (!isEnabled) InlineInputDisabledVariant else null,
        )
            .resize(Resize.None)
            .then(modifier)
            .toAttrs {
                placeholder(placeholderText)
                value(value)
                if (!isEnabled) disabled()
                if (isReadOnly) readOnly()
                onInput {
                    val newValue = if (transformValue != null) {
                        transformValue(it.value)
                    } else {
                        it.value
                    }
                    onValueChange(newValue)
                }
            }
    )
}



sealed interface InlineInputKind : ComponentKind

val InlineInputStyle = CssStyle<InlineInputKind> {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .fillMaxWidth()
            .padding(leftRight = Dimens.inputIndent, topBottom = Dimens.inputWrapping)
            .fontSize(Dimens.inputDefaultFontSize)
            .borderRadius(Dimens.inputBorderRadius)
            .border(
                width = Dimens.inputBorderWidth,
                style = LineStyle.Solid,
                color = palette.border
            )
            .color(palette.color)
            .transition(Transition.all(Durations.short))
    }

    focus {
        Modifier.border(
            width = Dimens.inputBorderWidth,
            style = LineStyle.Solid,
            color = palette.primary
        )
            .outline(Outline.Unset)
    }

    placeholder {
        Modifier.color(palette.placeholder)
    }
}
val InlineInputGhostVariant = InlineInputStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .border(
                width = Dimens.inputBorderWidth,
                style = LineStyle.Solid,
                color = palette.transparent,
            )
    }
    focus {
        Modifier.border(
            width = Dimens.inputBorderWidth,
            style = LineStyle.Solid,
            color = palette.primary
        )
            .outline(Outline.Unset)
    }
}
val InlineInputCompactVariant = InlineInputStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .padding(leftRight = Dimens.inputIndent / 2, topBottom = Dimens.inputWrapping / 2)
    }
}
val InlineInputDisabledVariant = InlineInputStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .opacity(0.5)
            .cursor(Cursor.NotAllowed)
            .backgroundColor(palette.color.toRgb().copyf(alpha = 0.05f))
    }
}
val InlineInputReadOnlyVariant = InlineInputStyle.addVariant {
    val palette = colorMode.toPalette()
    base {
        Modifier
            .backgroundColor(palette.transparent)
    }
    focus {
        Modifier.border(
            width = Dimens.inputBorderWidth,
            style = LineStyle.Solid,
            color = palette.transparent,
        )
    }
}
